import { useState } from 'react';
import { useCreditsStore } from '../../store/credits';
import { useCredits } from '../../hooks/useCredits';
import { TIER_INFO, TIER_CREDITS, POLAR_PRODUCT_IDS, type SubscriptionTier } from '@n8n-builder/shared';
import { API_BASE_URL } from '../../lib/api-config';
import { useSettingsStore } from '../../store/settings';

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

const TIER_FEATURES: Record<SubscriptionTier, string[]> = {
  free: ['Basic workflow generation', 'Community support'],
  pro: [
    'Unlimited workflows',
    '811+ n8n node support',
    'Plan, Build, and Edit modes',
    'Direct deployment to your n8n instance',
    'Import your existing workflows',
'Workflow edit mode',
    'Email support',
  ],
  business: [
    'Everything in Pro, plus:',
    'Unlimited conversation history',
    'Priority processing',
    'Use the latest Opus model',
    'Connect multiple N8N instances',
    'Priority support',
  ],
};

export function SubscriptionTab() {
  const { credits, maxCredits, subscription } = useCreditsStore();
  const { closeSettingsModal } = useSettingsStore();
  useCredits();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const currentTier = (subscription || 'free') as SubscriptionTier;
  const currentInfo = TIER_INFO[currentTier];
  const { refetch: refreshCredits } = useCredits();

  const handleUpgrade = async (tier: 'pro' | 'business') => {
    setCheckoutLoading(tier);
    setError(null);
    setSuccessMessage(null);

    try {
      if (currentTier !== 'free') {
        // Confirm before changing plan
        const targetInfo = TIER_INFO[tier];
        const tierOrder = { free: 0, pro: 1, business: 2 } as const;
        const isUpgrade = tierOrder[tier] > tierOrder[currentTier];
        const confirmMessage = isUpgrade
          ? `Upgrade to ${targetInfo.name} ($${targetInfo.price}/mo)?\n\nYou'll be charged the prorated difference immediately.`
          : `Downgrade to ${targetInfo.name} ($${targetInfo.price}/mo)?\n\nYou'll keep your current plan and credits until the end of your billing period.`;
        const confirmed = window.confirm(confirmMessage);
        if (!confirmed) {
          setCheckoutLoading(null);
          return;
        }

        // Existing subscriber: change plan server-side with proration
        const res = await fetch(`${API_BASE_URL}/api/billing/change-plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ productId: POLAR_PRODUCT_IDS[tier] }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${res.status}`);
        }

        const data = await res.json();
        if (data.immediate) {
          setSuccessMessage(`Upgraded to ${targetInfo.name}! Your credits and billing have been updated.`);
          setTimeout(() => refreshCredits(), 2000);
        } else {
          setSuccessMessage(`Your plan will switch to ${targetInfo.name} at the end of your current billing period. You'll keep your current credits until then.`);
        }
      } else {
        // Free user: open embedded checkout
        const res = await fetch(`${API_BASE_URL}/api/billing/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ productId: POLAR_PRODUCT_IDS[tier] }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${res.status}`);
        }

        const { url } = await res.json();
        closeSettingsModal();
        const { PolarEmbedCheckout } = await import('@polar-sh/checkout/embed');
        PolarEmbedCheckout.create(url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/billing/portal`, {
        method: 'GET',
        credentials: 'include',
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err) {
      console.error('Portal error:', err);
      setError(err instanceof Error ? err.message : 'Unable to open billing portal.');
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Subscription</h2>
        <p className="text-sm text-gray-500">Manage your plan and billing.</p>
      </div>

      {error && (
        <div className="border border-red-200 rounded-xl p-4 bg-red-50">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="border border-green-200 rounded-xl p-4 bg-green-50">
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Current plan summary */}
      <div className="border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900">{currentInfo.name}</h3>
              <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800">
                Current plan
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{currentInfo.description}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">
              {currentInfo.price === 0 ? 'Free' : `$${currentInfo.price}`}
            </div>
            {currentInfo.price > 0 && <div className="text-sm text-gray-500">/month</div>}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <span className="font-medium text-gray-900">{credits}</span> credits remaining
            {maxCredits > 0 && <span className="text-gray-400"> (max {maxCredits})</span>}
          </div>
          {currentTier !== 'free' && (
            <button
              onClick={handleManageBilling}
              disabled={portalLoading}
              className="text-sm font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
            >
              {portalLoading ? 'Opening...' : 'Manage billing'}
            </button>
          )}
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-2 gap-4">
        {(['pro', 'business'] as const)
          .filter((tier) => tier !== currentTier)
          .map((tier) => {
            const info = TIER_INFO[tier];
            const tierOrder = { free: 0, pro: 1, business: 2 } as const;
            const isUpgrade = tierOrder[tier] > tierOrder[currentTier];
            const features = TIER_FEATURES[tier];

            return (
              <div
                key={tier}
                className="border border-gray-200 rounded-xl p-5 flex flex-col"
              >
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900">{info.name}</h4>
                  <div className="mt-1">
                    <span className="text-2xl font-bold text-gray-900">
                      {info.price === 0 ? '$0' : `$${info.price}`}
                    </span>
                    <span className="text-sm text-gray-500">/mo</span>
                  </div>
                </div>

                <div className="mb-4 pb-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{TIER_CREDITS[tier]} credits/month</p>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(tier as 'pro' | 'business')}
                  disabled={checkoutLoading !== null}
                  className={isUpgrade
                    ? "w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    : "w-full text-center text-sm font-medium text-gray-500 hover:text-gray-700 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  }
                >
                  {checkoutLoading === tier ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Loading...
                    </span>
                  ) : (
                    `${isUpgrade ? 'Upgrade' : 'Downgrade'} to ${info.name}`
                  )}
                </button>
              </div>
            );
          })}
      </div>
    </div>
  );
}
